import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AttendanceLocations from "@/components/AttendanceLocations";
import MyAttendanceList from "@/components/MyAttendanceList";
import MyAttendanceWidget from "@/components/MyAttendanceWidget";
import { computeWorkHours } from "@/utils/attendanceHours";
import { 
  Plus, Search, Calendar, Clock, CheckCircle2, XCircle, Trash2, 
  User, Mail, Phone, Building, Briefcase, IndianRupee, FileText,
  Upload, Eye, Edit, MapPin, UserPlus, Link, Download, LogIn, LogOut
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeamPage() {
  const { token, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [timelogs, setTimelogs] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusConfig, setStatusConfig] = useState({});
  const [myAttendanceStatus, setMyAttendanceStatus] = useState(null);
  
  // Dialogs
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [timelogDialog, setTimelogDialog] = useState(false);
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [editAttendance, setEditAttendance] = useState(null); // { id, check_in, check_out, status, notes, employee_name, date }
  const [myAttendanceDialog, setMyAttendanceDialog] = useState(false);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [employeeDetailDialog, setEmployeeDetailDialog] = useState(false);
  const [kycDialog, setKycDialog] = useState(false);
  
  // Attendance filters (for Admin/HR)
  const [attendanceFilterFromDate, setAttendanceFilterFromDate] = useState("");
  const [attendanceFilterToDate, setAttendanceFilterToDate] = useState("");
  const [attendanceFilterEmployee, setAttendanceFilterEmployee] = useState("all");
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [createUserForEmployee, setCreateUserForEmployee] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  
  // Forms
  const [leaveForm, setLeaveForm] = useState({
    leave_type: "Casual", start_date: "", end_date: "", days: 1, reason: ""
  });
  
  const [timelogForm, setTimelogForm] = useState({
    date: new Date().toISOString().split('T')[0], task: "", hours: 8, description: ""
  });
  
  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: "", date: new Date().toISOString().split('T')[0], 
    check_in: "09:00", check_out: "18:00", status: "Present"
  });
  
  const [myAttendanceForm, setMyAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    check_in: "09:00", check_out: "18:00", status: "Present"
  });
  
  const [employeeForm, setEmployeeForm] = useState({
    name: "", email: "", phone: "", department: "", designation: "",
    join_date: new Date().toISOString().split('T')[0], employment_type: "Full Time",
    employee_code: "", // Manual Employee ID
    salary: "", bank_name: "", bank_account: "", ifsc_code: "", pan_number: "",
    date_of_birth: "", gender: "", address: "", city: "", state: "", pincode: "",
    emergency_contact: "", emergency_phone: "", password: "password123"
  });
  
  const [kycForm, setKycForm] = useState({
    document_type: "Aadhaar", document_name: "", file: null
  });

  const isAdmin = user?.role === "Admin";
  const isHRManager = user?.role === "HR Manager";
  const canManageHR = isAdmin || isHRManager;

  useEffect(() => {
    fetchData();
    fetchMyAttendanceStatus();
    fetchStatusConfig();
  }, []);

  const fetchStatusConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/status-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusConfig(res.data || {});
    } catch (error) {
      console.error("Failed to fetch status config", error);
    }
  };

  const fetchMyAttendanceStatus = async () => {
    try {
      const res = await axios.get(`${API}/hr/attendance/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAttendanceStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch attendance status", error);
    }
  };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/leaves`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/timelogs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/attendance`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      
      if (isAdmin || isHRManager) {
        requests.push(axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } }));
      }
      
      const responses = await Promise.all(requests);
      setEmployees(responses[0].data);
      setLeaves(responses[1].data);
      setTimelogs(responses[2].data);
      setAttendance(responses[3].data);
      if ((isAdmin || isHRManager) && responses[4]) {
        setUsers(responses[4].data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Get users who are not yet employees
  const getAvailableUsersForLinking = () => {
    const employeeEmails = employees.map(e => e.email.toLowerCase());
    return users.filter(u => !employeeEmails.includes(u.email.toLowerCase()));
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      name: "", email: "", phone: "", department: "", designation: "",
      join_date: new Date().toISOString().split('T')[0], employment_type: "Full Time",
      employee_code: "",
      salary: "", bank_name: "", bank_account: "", ifsc_code: "", pan_number: "",
      date_of_birth: "", gender: "", address: "", city: "", state: "", pincode: "",
      emergency_contact: "", emergency_phone: "", password: "password123"
    });
    setEditingEmployee(null);
    setCreateUserForEmployee(true);
    setSelectedUserId("");
  };

  // Check-in handler
  // Handle my own attendance submission (for any employee)
  const handleMyAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hr/attendance/self`, myAttendanceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Attendance marked successfully");
      setMyAttendanceDialog(false);
      setMyAttendanceForm({
        date: new Date().toISOString().split('T')[0],
        check_in: "09:00", check_out: "18:00", status: "Present"
      });
      fetchMyAttendanceStatus();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  // Export employees to Excel
  const handleExportEmployees = async () => {
    try {
      const response = await axios.get(`${API}/hr/employees/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Employees exported successfully");
    } catch (error) {
      toast.error("Failed to export employees");
    }
  };

  // Export attendance to Excel (respects the on-screen date-range + employee filter)
  const handleExportAttendance = async () => {
    try {
      const params = {};
      if (attendanceFilterFromDate) params.start_date = attendanceFilterFromDate;
      if (attendanceFilterToDate) params.end_date = attendanceFilterToDate;
      if (attendanceFilterEmployee && attendanceFilterEmployee !== "all") {
        params.employee_id = attendanceFilterEmployee;
      }
      const response = await axios.get(`${API}/hr/attendance/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = attendanceFilterFromDate || attendanceFilterToDate
        ? `_${attendanceFilterFromDate || 'start'}_to_${attendanceFilterToDate || 'end'}`
        : '';
      link.setAttribute('download', `attendance${suffix}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Attendance exported successfully");
    } catch (error) {
      toast.error("Failed to export attendance");
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      const selectedUser = users.find(u => u.id === userId);
      if (selectedUser) {
        setEmployeeForm(prev => ({
          ...prev,
          name: selectedUser.name,
          email: selectedUser.email,
          user_id: selectedUser.id
        }));
      }
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...employeeForm,
        salary: employeeForm.salary ? parseFloat(employeeForm.salary) : null,
        date_of_birth: employeeForm.date_of_birth || null
      };
      
      if (editingEmployee) {
        await axios.put(`${API}/hr/employees/${editingEmployee.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Employee updated successfully");
      } else {
        // If creating new employee and user account needed
        if (createUserForEmployee && !selectedUserId) {
          // Create user first, then employee
          await axios.post(`${API}/hr/employees/with-user`, {
            ...payload,
            role: "Viewer",
            assigned_modules: ["dashboard", "team"]
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Employee and user account created successfully");
        } else {
          // Link to existing user
          payload.user_id = selectedUserId;
          await axios.post(`${API}/hr/employees`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Employee created successfully");
        }
      }
      setEmployeeDialog(false);
      resetEmployeeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await axios.delete(`${API}/hr/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Employee deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete employee");
    }
  };

  const handleEditEmployee = (emp) => {
    setEmployeeForm({
      name: emp.name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      department: emp.department || "",
      designation: emp.designation || "",
      join_date: emp.join_date || "",
      employment_type: emp.employment_type || "Full Time",
      salary: emp.salary || "",
      bank_name: emp.bank_name || "",
      bank_account: emp.bank_account || "",
      ifsc_code: emp.ifsc_code || "",
      pan_number: emp.pan_number || "",
      date_of_birth: emp.date_of_birth || "",
      gender: emp.gender || "",
      address: emp.address || "",
      city: emp.city || "",
      state: emp.state || "",
      pincode: emp.pincode || "",
      emergency_contact: emp.emergency_contact || "",
      emergency_phone: emp.emergency_phone || "",
      password: ""
    });
    setEditingEmployee(emp);
    setEmployeeDialog(true);
  };

  const handleViewEmployee = (emp) => {
    setSelectedEmployee(emp);
    setEmployeeDetailDialog(true);
  };

  const handleKycUpload = async (e) => {
    e.preventDefault();
    if (!kycForm.file || !selectedEmployee) return;
    
    try {
      const formData = new FormData();
      formData.append('file', kycForm.file);
      formData.append('document_type', kycForm.document_type);
      formData.append('document_name', kycForm.document_name);
      
      await axios.post(`${API}/hr/employees/${selectedEmployee.id}/kyc`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("Document uploaded successfully");
      setKycDialog(false);
      setKycForm({ document_type: "Aadhaar", document_name: "", file: null });
      
      const empRes = await axios.get(`${API}/hr/employees/${selectedEmployee.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployee(empRes.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    }
  };

  const handleDeleteKyc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/hr/employees/${selectedEmployee.id}/kyc/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      
      const empRes = await axios.get(`${API}/hr/employees/${selectedEmployee.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployee(empRes.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hr/leaves`, leaveForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Leave request submitted");
      setLeaveDialog(false);
      setLeaveForm({ leave_type: "Casual", start_date: "", end_date: "", days: 1, reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    try {
      await axios.put(`${API}/hr/leaves/${leaveId}`, {
        status: action,
        approved_by: user.id,
        approval_date: new Date().toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Leave ${action.toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update leave");
    }
  };

  const handleTimelogSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hr/timelogs`, timelogForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Time logged successfully");
      setTimelogDialog(false);
      setTimelogForm({ date: new Date().toISOString().split('T')[0], task: "", hours: 8, description: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to log time");
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hr/attendance`, attendanceForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Attendance marked");
      setAttendanceDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const handleEditAttendanceSubmit = async (e) => {
    e.preventDefault();
    if (!editAttendance) return;
    try {
      const payload = {
        check_in: editAttendance.check_in || null,
        check_out: editAttendance.check_out || null,
        status: editAttendance.status,
        notes: editAttendance.notes || null,
      };
      await axios.put(`${API}/hr/attendance/${editAttendance.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Attendance updated");
      setEditAttendance(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update attendance");
    }
  };

  const handleDeleteLeave = async (id) => {
    if (!window.confirm("Delete this leave request?")) return;
    try {
      await axios.delete(`${API}/hr/leaves/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Leave deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteTimelog = async (id) => {
    if (!window.confirm("Delete this time log?")) return;
    try {
      await axios.delete(`${API}/hr/timelogs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Time log deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if employee has a linked user account
  const hasUserAccount = (emp) => {
    return users.some(u => u.email.toLowerCase() === emp.email.toLowerCase());
  };

  const statusColors = {
    "Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "Approved": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Rejected": "bg-red-50 text-red-700 border-red-200",
    "Present": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Absent": "bg-red-50 text-red-700 border-red-200",
    "Half Day": "bg-amber-50 text-amber-700 border-amber-200",
    "Leave": "bg-blue-50 text-blue-700 border-blue-200",
  };

  const availableUsers = getAvailableUsersForLinking();

  if (loading) return <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen text-slate-600 dark:text-slate-400">Loading...</div>;

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Team & HR</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage employees, leaves, time logs, and attendance</p>
      </div>

      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="mb-6 dark:bg-slate-800">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">My Leaves</TabsTrigger>
          {canManageHR && <TabsTrigger value="leave-approvals" data-testid="tab-leave-approvals">Leave Approvals</TabsTrigger>}
          <TabsTrigger value="timelogs" data-testid="tab-timelogs">Time Logs</TabsTrigger>
          {myAttendanceStatus?.has_employee_record && (
            <TabsTrigger value="my-attendance" data-testid="tab-my-attendance">My Attendance</TabsTrigger>
          )}
          {canManageHR && <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>}
          {canManageHR && <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>}
        </TabsList>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees">
          {/* Geo-fenced Check-In / Check-Out widget (replaces legacy "Mark My Attendance") */}
          <MyAttendanceWidget />

          <div className="flex items-center justify-between mb-6">
            <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg flex-1 mr-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search employees by name, email, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="employee-search-input"
                  className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
                />
              </div>
            </Card>
            <div className="flex gap-2">
              {canManageHR && (
                <Button 
                  variant="outline"
                  onClick={handleExportEmployees}
                  data-testid="export-employees-btn"
                  className="h-10"
                >
                  <Download className="h-4 w-4 mr-2" />Export
                </Button>
              )}
              {canManageHR && (
                <Dialog open={employeeDialog} onOpenChange={(open) => {
                  setEmployeeDialog(open);
                  if (!open) resetEmployeeForm();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="add-employee-button"
                      className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium"
                    >
                      <Plus className="h-4 w-4 mr-2" />Add Employee
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader>
                      <DialogTitle>{editingEmployee ? "Edit Employee" : "Add New Employee"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEmployeeSubmit} className="space-y-6">
                      
                      {/* User Account Linking (only for new employees) */}
                      {!editingEmployee && (
                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <UserPlus className="h-4 w-4" />User Account
                          </h3>
                          <p className="text-xs text-slate-500 mb-3">
                            Every employee needs a user account to login. Email is used to link user and employee records.
                          </p>
                          
                          <div className="space-y-3">
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="radio" 
                                  checked={createUserForEmployee} 
                                  onChange={() => { setCreateUserForEmployee(true); setSelectedUserId(""); }}
                                  className="text-brillien-600"
                                />
                                <span className="text-sm">Create new user account</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="radio" 
                                  checked={!createUserForEmployee} 
                                  onChange={() => setCreateUserForEmployee(false)}
                                  className="text-brillien-600"
                                />
                              <span className="text-sm">Link to existing user</span>
                            </label>
                          </div>
                          
                          {!createUserForEmployee && (
                            <div>
                              <Label>Select Existing User</Label>
                              <Select value={selectedUserId} onValueChange={handleUserSelect}>
                                <SelectTrigger className="rounded-lg">
                                  <SelectValue placeholder="Select a user to link" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableUsers.length > 0 ? (
                                    availableUsers.map(u => (
                                      <SelectItem key={u.id} value={u.id}>
                                        <div className="flex items-center gap-2">
                                          <User className="h-3.5 w-3.5 text-slate-400" />
                                          {u.name} ({u.email})
                                        </div>
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <SelectItem value="none" disabled>No available users to link</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-slate-500 mt-1">Only users without employee records are shown</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Basic Information */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" />Basic Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name *</Label>
                          <Input 
                            value={employeeForm.name} 
                            onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})} 
                            required 
                            className="rounded-lg" 
                            data-testid="employee-name-input"
                            disabled={!createUserForEmployee && selectedUserId}
                          />
                        </div>
                        <div>
                          <Label>Email * <span className="text-xs text-slate-500">(Used for login)</span></Label>
                          <Input 
                            type="email" 
                            value={employeeForm.email} 
                            onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})} 
                            required 
                            className="rounded-lg" 
                            data-testid="employee-email-input"
                            disabled={!createUserForEmployee && selectedUserId}
                          />
                        </div>
                        {createUserForEmployee && !editingEmployee && (
                          <div>
                            <Label>Password * <span className="text-xs text-slate-500">(For login)</span></Label>
                            <Input 
                              type="password" 
                              value={employeeForm.password} 
                              onChange={(e) => setEmployeeForm({...employeeForm, password: e.target.value})} 
                              required 
                              className="rounded-lg"
                              placeholder="Min 6 characters"
                            />
                          </div>
                        )}
                        <div>
                          <Label>Phone *</Label>
                          <Input value={employeeForm.phone} onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})} required className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Date of Birth</Label>
                          <Input type="date" value={employeeForm.date_of_birth} onChange={(e) => setEmployeeForm({...employeeForm, date_of_birth: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Gender</Label>
                          <Select value={employeeForm.gender} onValueChange={(v) => setEmployeeForm({...employeeForm, gender: v})}>
                            <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select Gender" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Employment Details */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />Employment Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Employee ID (Manual)</Label>
                          <Input 
                            value={employeeForm.employee_code} 
                            onChange={(e) => setEmployeeForm({...employeeForm, employee_code: e.target.value})} 
                            className="rounded-lg" 
                            placeholder="e.g., BRL-001"
                          />
                          <p className="text-xs text-slate-500 mt-1">Optional custom employee ID</p>
                        </div>
                        <div>
                          <Label>Department *</Label>
                          <Select value={employeeForm.department} onValueChange={(v) => setEmployeeForm({...employeeForm, department: v})}>
                            <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select Department" /></SelectTrigger>
                            <SelectContent>
                              {(statusConfig.hr?.department || [
                                {value: "Engineering", label: "Engineering"},
                                {value: "Design", label: "Design"},
                                {value: "Finance", label: "Finance"},
                                {value: "HR", label: "HR"},
                                {value: "Operations", label: "Operations"},
                                {value: "Sales", label: "Sales"},
                                {value: "Admin", label: "Admin"}
                              ]).map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Designation *</Label>
                          <Input value={employeeForm.designation} onChange={(e) => setEmployeeForm({...employeeForm, designation: e.target.value})} required className="rounded-lg" placeholder="e.g., Senior Engineer" />
                        </div>
                        <div>
                          <Label>Join Date *</Label>
                          <Input type="date" value={employeeForm.join_date} onChange={(e) => setEmployeeForm({...employeeForm, join_date: e.target.value})} required className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Employment Type</Label>
                          <Select value={employeeForm.employment_type} onValueChange={(v) => setEmployeeForm({...employeeForm, employment_type: v})}>
                            <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(statusConfig.hr?.employment_type || [
                                {value: "Full Time", label: "Full Time"},
                                {value: "Part Time", label: "Part Time"},
                                {value: "Contract", label: "Contract"},
                                {value: "Intern", label: "Intern"}
                              ]).map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Salary & Bank Details */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <IndianRupee className="h-4 w-4" />Salary & Bank Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Monthly Salary (₹)</Label>
                          <Input type="number" value={employeeForm.salary} onChange={(e) => setEmployeeForm({...employeeForm, salary: e.target.value})} className="rounded-lg" placeholder="e.g., 50000" />
                        </div>
                        <div>
                          <Label>PAN Number</Label>
                          <Input value={employeeForm.pan_number} onChange={(e) => setEmployeeForm({...employeeForm, pan_number: e.target.value.toUpperCase()})} className="rounded-lg" placeholder="ABCDE1234F" maxLength={10} />
                        </div>
                        <div>
                          <Label>Bank Name</Label>
                          <Input value={employeeForm.bank_name} onChange={(e) => setEmployeeForm({...employeeForm, bank_name: e.target.value})} className="rounded-lg" placeholder="e.g., HDFC Bank" />
                        </div>
                        <div>
                          <Label>Bank Account No.</Label>
                          <Input value={employeeForm.bank_account} onChange={(e) => setEmployeeForm({...employeeForm, bank_account: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>IFSC Code</Label>
                          <Input value={employeeForm.ifsc_code} onChange={(e) => setEmployeeForm({...employeeForm, ifsc_code: e.target.value.toUpperCase()})} className="rounded-lg" placeholder="HDFC0001234" />
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />Address & Emergency Contact
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Address</Label>
                          <Textarea value={employeeForm.address} onChange={(e) => setEmployeeForm({...employeeForm, address: e.target.value})} className="rounded-lg" rows={2} />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input value={employeeForm.city} onChange={(e) => setEmployeeForm({...employeeForm, city: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>State</Label>
                          <Input value={employeeForm.state} onChange={(e) => setEmployeeForm({...employeeForm, state: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Pincode</Label>
                          <Input value={employeeForm.pincode} onChange={(e) => setEmployeeForm({...employeeForm, pincode: e.target.value})} className="rounded-lg" maxLength={6} />
                        </div>
                        <div>
                          <Label>Emergency Contact Name</Label>
                          <Input value={employeeForm.emergency_contact} onChange={(e) => setEmployeeForm({...employeeForm, emergency_contact: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Emergency Contact Phone</Label>
                          <Input value={employeeForm.emergency_phone} onChange={(e) => setEmployeeForm({...employeeForm, emergency_phone: e.target.value})} className="rounded-lg" />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => setEmployeeDialog(false)} className="rounded-lg">Cancel</Button>
                      <Button type="submit" data-testid="employee-submit-button" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">
                        {editingEmployee ? "Update Employee" : "Create Employee"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden bg-white dark:bg-slate-800">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Employee</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Department</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Contact</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Join Date</th>
                  <th className="text-center p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Status</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredEmployees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`employee-row-${emp.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-brillien-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-brillien-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{emp.name}</p>
                          <p className="text-sm text-brillien-600 dark:text-brillien-400">{emp.designation}</p>
                          <div className="flex gap-2 text-xs text-slate-400">
                            {emp.employee_code && <span className="font-mono bg-slate-100 px-1 rounded">{emp.employee_code}</span>}
                            <span className="font-mono">{emp.id}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-slate-900 dark:text-slate-200">{emp.department}</p>
                      <p className="text-xs text-slate-500">{emp.employment_type || "Full-time"}</p>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        <p className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                          <Mail className="h-3 w-3" />{emp.email}
                        </p>
                        <p className="text-slate-500 dark:text-slate-400 flex items-center gap-1 text-xs">
                          <Phone className="h-3 w-3" />{emp.phone}
                        </p>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-slate-600 dark:text-slate-300">{emp.join_date}</td>
                    <td className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Badge className={`rounded-full px-2 py-0.5 text-xs font-medium border ${emp.is_active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {emp.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                        {hasUserAccount(emp) && (
                          <Badge className="rounded-full px-2 py-0.5 text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
                            <Link className="h-3 w-3 mr-1 inline" />Login
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleViewEmployee(emp)} className="rounded-lg text-xs h-8" data-testid={`view-employee-${emp.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" />View
                        </Button>
                        {canManageHR && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleEditEmployee(emp)} className="h-8 w-8 p-0" data-testid={`edit-employee-${emp.id}`}>
                              <Edit className="h-4 w-4 text-slate-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEmployee(emp.id)} className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-employee-${emp.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <User className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p>No employees found</p>
              {canManageHR && <p className="text-sm mt-1">Click "Add Employee" to create one</p>}
            </div>
          )}
        </TabsContent>

        {/* Employee Detail Dialog */}
        <Dialog open={employeeDetailDialog} onOpenChange={setEmployeeDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
            <DialogHeader>
              <DialogTitle>Employee Details</DialogTitle>
            </DialogHeader>
            {selectedEmployee && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-start gap-4 pb-4 border-b">
                  <div className="h-16 w-16 rounded-full bg-brillien-100 flex items-center justify-center">
                    <User className="h-8 w-8 text-brillien-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900">{selectedEmployee.name}</h2>
                    <p className="text-brillien-600 font-medium">{selectedEmployee.designation}</p>
                    <p className="text-sm text-slate-500">{selectedEmployee.department} • {selectedEmployee.employment_type}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge className={`rounded-full ${selectedEmployee.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {selectedEmployee.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                    {hasUserAccount(selectedEmployee) && (
                      <Badge className="rounded-full bg-blue-50 text-blue-700">
                        <Link className="h-3 w-3 mr-1 inline" />Has Login
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <p><span className="text-slate-500">Email:</span> {selectedEmployee.email}</p>
                    <p><span className="text-slate-500">Phone:</span> {selectedEmployee.phone}</p>
                    <p><span className="text-slate-500">Join Date:</span> {selectedEmployee.join_date}</p>
                    <p><span className="text-slate-500">DOB:</span> {selectedEmployee.date_of_birth || '-'}</p>
                    <p><span className="text-slate-500">Gender:</span> {selectedEmployee.gender || '-'}</p>
                  </div>
                </div>

                {/* Address */}
                {selectedEmployee.address && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Address</h3>
                    <p className="text-sm text-slate-600">
                      {selectedEmployee.address}, {selectedEmployee.city}, {selectedEmployee.state} - {selectedEmployee.pincode}
                    </p>
                  </div>
                )}

                {/* Salary Info (Admin/HR only) */}
                {canManageHR && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <IndianRupee className="h-4 w-4" />Salary & Bank Details
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <p><span className="text-slate-500">Monthly Salary:</span> <span className="font-semibold text-slate-900">₹{selectedEmployee.salary?.toLocaleString() || '-'}</span></p>
                      <p><span className="text-slate-500">PAN:</span> {selectedEmployee.pan_number || '-'}</p>
                      <p><span className="text-slate-500">Bank:</span> {selectedEmployee.bank_name || '-'}</p>
                      <p><span className="text-slate-500">Account:</span> {selectedEmployee.bank_account || '-'}</p>
                      <p><span className="text-slate-500">IFSC:</span> {selectedEmployee.ifsc_code || '-'}</p>
                    </div>
                  </div>
                )}

                {/* Emergency Contact */}
                {selectedEmployee.emergency_contact && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Emergency Contact</h3>
                    <p className="text-sm text-slate-600">{selectedEmployee.emergency_contact} - {selectedEmployee.emergency_phone}</p>
                  </div>
                )}

                {/* KYC Documents (Admin/HR only) */}
                {canManageHR && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <FileText className="h-4 w-4" />KYC Documents
                      </h3>
                      <Dialog open={kycDialog} onOpenChange={setKycDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg text-xs" data-testid="upload-kyc-button">
                            <Upload className="h-3.5 w-3.5 mr-1" />Upload Document
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Upload KYC Document</DialogTitle></DialogHeader>
                          <form onSubmit={handleKycUpload} className="space-y-4">
                            <div>
                              <Label>Document Type *</Label>
                              <Select value={kycForm.document_type} onValueChange={(v) => setKycForm({...kycForm, document_type: v})}>
                                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Aadhaar">Aadhaar Card</SelectItem>
                                  <SelectItem value="PAN">PAN Card</SelectItem>
                                  <SelectItem value="Passport">Passport</SelectItem>
                                  <SelectItem value="Driving License">Driving License</SelectItem>
                                  <SelectItem value="Voter ID">Voter ID</SelectItem>
                                  <SelectItem value="Bank Statement">Bank Statement</SelectItem>
                                  <SelectItem value="Address Proof">Address Proof</SelectItem>
                                  <SelectItem value="Photo">Photo</SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Document Name *</Label>
                              <Input 
                                value={kycForm.document_name} 
                                onChange={(e) => setKycForm({...kycForm, document_name: e.target.value})} 
                                required 
                                className="rounded-lg"
                                placeholder="e.g., Aadhaar Front Side"
                              />
                            </div>
                            <div>
                              <Label>Upload File *</Label>
                              <Input 
                                type="file" 
                                onChange={(e) => setKycForm({...kycForm, file: e.target.files[0]})} 
                                required 
                                className="rounded-lg"
                                accept=".pdf,.jpg,.jpeg,.png"
                              />
                              <p className="text-xs text-slate-500 mt-1">Accepted: PDF, JPG, PNG (Max 5MB)</p>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="outline" onClick={() => setKycDialog(false)} className="rounded-lg">Cancel</Button>
                              <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">Upload</Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    
                    {selectedEmployee.kyc_documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedEmployee.kyc_documents.map((doc, idx) => (
                          <div key={doc.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                                <p className="text-xs text-slate-500">{doc.document_type} • {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <a href={`${BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="rounded-lg text-xs">
                                  <Eye className="h-3.5 w-3.5 mr-1" />View
                                </Button>
                              </a>
                              <Button variant="outline" size="sm" onClick={() => handleDeleteKyc(doc.id)} className="rounded-lg text-xs text-red-600 hover:bg-red-50 border-red-200">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">No documents uploaded yet</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* MY LEAVES TAB */}
        <TabsContent value="leaves">
          <div className="flex justify-end mb-6">
            <Dialog open={leaveDialog} onOpenChange={setLeaveDialog}>
              <DialogTrigger asChild>
                <Button className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium">
                  <Plus className="h-4 w-4 mr-2" />Request Leave
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
                <form onSubmit={handleLeaveSubmit} className="space-y-4">
                  <div>
                    <Label>Leave Type *</Label>
                    <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(statusConfig.hr?.leave_type || [
                          {value: "Casual", label: "Casual Leave"},
                          {value: "Sick", label: "Sick Leave"},
                          {value: "Earned", label: "Earned Leave"},
                          {value: "Unpaid", label: "Unpaid Leave"}
                        ]).map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date *</Label>
                      <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required className="rounded-lg" />
                    </div>
                    <div>
                      <Label>End Date *</Label>
                      <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required className="rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <Label>Number of Days *</Label>
                    <Input type="number" min="1" value={leaveForm.days} onChange={(e) => setLeaveForm({...leaveForm, days: parseInt(e.target.value)})} required className="rounded-lg" />
                  </div>
                  <div>
                    <Label>Reason *</Label>
                    <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} required className="rounded-lg" rows={3} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setLeaveDialog(false)} className="rounded-lg">Cancel</Button>
                    <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">Submit Request</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {(() => {
              // Show only the current employee's leaves.
              // For non-HR users, the backend already returns just their own — but for HR
              // who are also employees, we still want this tab to show "My Leaves".
              const myEmpId = myAttendanceStatus?.employee_id;
              const myLeaves = myEmpId
                ? leaves.filter(l => l.employee_id === myEmpId)
                : (!canManageHR ? leaves : []);

              if (myLeaves.length === 0) {
                return (
                  <Card className="p-12 border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-center">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                    <p className="text-slate-600 dark:text-slate-300 font-medium">No leave requests yet</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Click <strong>Request Leave</strong> above to submit your first request.
                    </p>
                  </Card>
                );
              }

              return myLeaves
                .slice()
                .sort((a, b) => (b.created_at || b.start_date || '').localeCompare(a.created_at || a.start_date || ''))
                .map((leave) => (
                  <Card key={leave.id} className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg" data-testid={`my-leave-${leave.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{leave.id}</span>
                          <Badge className={`${statusColors[leave.status] || 'bg-slate-100 text-slate-700 border-slate-200'} rounded-full px-2 py-0.5 text-xs font-medium border`} data-testid={`my-leave-status-${leave.id}`}>
                            {leave.status}
                          </Badge>
                          {leave.approved_by && leave.status !== 'Pending' && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              by {leave.approved_by}
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
                          {leave.leave_type} Leave
                        </h3>
                      </div>
                      {leave.status === "Pending" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLeave(leave.id)}
                          className="text-red-600 h-7 w-7 p-0"
                          data-testid={`cancel-leave-${leave.id}`}
                          title="Cancel this pending leave request"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="text-slate-700 dark:text-slate-300">
                        <Calendar className="inline h-4 w-4 mr-2 text-slate-400" />
                        {leave.start_date} to {leave.end_date} ({leave.days} {leave.days === 1 ? 'day' : 'days'})
                      </p>
                      <p className="text-slate-700 dark:text-slate-300">
                        <span className="font-medium">Reason:</span> {leave.reason}
                      </p>
                      {leave.created_at && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Requested on {new Date(leave.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                  </Card>
                ));
            })()}
          </div>
        </TabsContent>

        {/* LEAVE APPROVALS TAB (Admin/HR only) */}
        {canManageHR && (
          <TabsContent value="leave-approvals">
            <div className="grid grid-cols-1 gap-4">
              {leaves.filter(l => l.status === "Pending").map((leave) => (
                <Card key={leave.id} className="p-6 border-slate-200 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-xs text-slate-600">{leave.id}</span>
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 rounded-full px-2 py-0.5 text-xs font-medium border">Pending Approval</Badge>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900">{leave.employee_name}</h3>
                      <p className="text-sm text-slate-600">{leave.leave_type} Leave</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <p><Calendar className="inline h-4 w-4 mr-2 text-slate-400" />{leave.start_date} to {leave.end_date} ({leave.days} days)</p>
                    <p className="text-slate-700"><strong>Reason:</strong> {leave.reason}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleLeaveAction(leave.id, "Approved")} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                      <CheckCircle2 className="h-4 w-4 mr-2" />Approve
                    </Button>
                    <Button onClick={() => handleLeaveAction(leave.id, "Rejected")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 rounded-lg">
                      <XCircle className="h-4 w-4 mr-2" />Reject
                    </Button>
                  </div>
                </Card>
              ))}
              {leaves.filter(l => l.status === "Pending").length === 0 && (
                <p className="text-center text-slate-500 py-8">No pending leave requests</p>
              )}
            </div>
          </TabsContent>
        )}

        {/* TIME LOGS TAB */}
        <TabsContent value="timelogs">
          <div className="flex justify-end mb-6">
            <Dialog open={timelogDialog} onOpenChange={setTimelogDialog}>
              <DialogTrigger asChild>
                <Button className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium">
                  <Plus className="h-4 w-4 mr-2" />Log Time
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Log Time</DialogTitle></DialogHeader>
                <form onSubmit={handleTimelogSubmit} className="space-y-4">
                  <div>
                    <Label>Date *</Label>
                    <Input type="date" value={timelogForm.date} onChange={(e) => setTimelogForm({...timelogForm, date: e.target.value})} required className="rounded-lg" />
                  </div>
                  <div>
                    <Label>Task *</Label>
                    <Input value={timelogForm.task} onChange={(e) => setTimelogForm({...timelogForm, task: e.target.value})} required className="rounded-lg" placeholder="e.g., Project planning" />
                  </div>
                  <div>
                    <Label>Hours *</Label>
                    <Input type="number" step="0.5" min="0.5" max="24" value={timelogForm.hours} onChange={(e) => setTimelogForm({...timelogForm, hours: parseFloat(e.target.value)})} required className="rounded-lg" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={timelogForm.description} onChange={(e) => setTimelogForm({...timelogForm, description: e.target.value})} className="rounded-lg" rows={3} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setTimelogDialog(false)} className="rounded-lg">Cancel</Button>
                    <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">Log Time</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {timelogs.map((log) => (
              <Card key={log.id} className="p-6 border-slate-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="font-mono text-sm font-semibold text-slate-900">{log.hours} hours</span>
                      <span className="text-sm text-slate-500">{log.date}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{log.task}</h3>
                    {log.description && <p className="text-sm text-slate-600 mt-1">{log.description}</p>}
                  </div>
                  {(log.employee_id === user?.id || isAdmin) && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTimelog(log.id)} className="text-red-600 h-7 w-7 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ATTENDANCE TAB (Admin/HR Manager only) */}
        {canManageHR && (
          <TabsContent value="attendance">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div>
                  <Label className="text-xs text-slate-500 dark:text-slate-400">From</Label>
                  <Input
                    type="date"
                    value={attendanceFilterFromDate}
                    onChange={(e) => setAttendanceFilterFromDate(e.target.value)}
                    className="h-10 w-40 rounded-lg dark:bg-slate-700 dark:border-slate-400"
                    data-testid="attendance-from-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 dark:text-slate-400">To</Label>
                  <Input
                    type="date"
                    value={attendanceFilterToDate}
                    onChange={(e) => setAttendanceFilterToDate(e.target.value)}
                    className="h-10 w-40 rounded-lg dark:bg-slate-700 dark:border-slate-400"
                    data-testid="attendance-to-date"
                  />
                </div>
                <Select value={attendanceFilterEmployee} onValueChange={setAttendanceFilterEmployee}>
                  <SelectTrigger className="h-10 w-48 rounded-lg dark:bg-slate-700 dark:border-slate-400 self-end">
                    <SelectValue placeholder="Filter by employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(attendanceFilterFromDate || attendanceFilterToDate || attendanceFilterEmployee !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setAttendanceFilterFromDate(""); setAttendanceFilterToDate(""); setAttendanceFilterEmployee("all"); }}
                    className="text-slate-500 self-end"
                    data-testid="attendance-clear-filters"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={handleExportAttendance}
                  data-testid="export-attendance-btn"
                  className="h-10"
                >
                  <Download className="h-4 w-4 mr-2" />Export
                </Button>
                <Dialog open={attendanceDialog} onOpenChange={setAttendanceDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium" data-testid="mark-attendance-button">
                      <Plus className="h-4 w-4 mr-2" />Mark Attendance
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                    <DialogHeader><DialogTitle>Mark Attendance</DialogTitle></DialogHeader>
                    <form onSubmit={handleAttendanceSubmit} className="space-y-4">
                      <div>
                        <Label>Employee *</Label>
                        <Select value={attendanceForm.employee_id} onValueChange={(v) => setAttendanceForm({...attendanceForm, employee_id: v})}>
                          <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                          <SelectContent>
                            {employees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.email})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input type="date" value={attendanceForm.date} onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})} required className="rounded-lg" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Check In</Label>
                          <Input type="time" value={attendanceForm.check_in} onChange={(e) => setAttendanceForm({...attendanceForm, check_in: e.target.value})} className="rounded-lg" />
                        </div>
                        <div>
                          <Label>Check Out</Label>
                          <Input type="time" value={attendanceForm.check_out} onChange={(e) => setAttendanceForm({...attendanceForm, check_out: e.target.value})} className="rounded-lg" />
                        </div>
                      </div>
                      <div>
                        <Label>Status *</Label>
                        <Select value={attendanceForm.status} onValueChange={(v) => setAttendanceForm({...attendanceForm, status: v})}>
                          <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Present">Present</SelectItem>
                            <SelectItem value="Absent">Absent</SelectItem>
                            <SelectItem value="Half Day">Half Day</SelectItem>
                            <SelectItem value="Leave">Leave</SelectItem>
                            <SelectItem value="Work From Home">Work From Home</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setAttendanceDialog(false)} className="rounded-lg">Cancel</Button>
                        <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">Mark Attendance</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            {/* Professional Attendance Table */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-400 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-700">
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Date</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Employee</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Check In</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Check Out</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Total Hours</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400">Marked By</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendance
                    .filter(att => {
                      const matchFrom = !attendanceFilterFromDate || att.date >= attendanceFilterFromDate;
                      const matchTo = !attendanceFilterToDate || att.date <= attendanceFilterToDate;
                      const matchEmployee = attendanceFilterEmployee === "all" || att.employee_id === attendanceFilterEmployee;
                      return matchFrom && matchTo && matchEmployee;
                    })
                    .map((att) => (
                      <TableRow key={att.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <TableCell className="font-medium text-slate-900 dark:text-slate-100 border-b border-slate-100 dark:border-slate-400">
                          {att.date}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-400">
                          {att.employee_name}
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-400">
                          {att.check_in || '-'}
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-400">
                          {att.check_out || '-'}
                        </TableCell>
                        <TableCell
                          className="text-slate-700 dark:text-slate-200 font-medium border-b border-slate-100 dark:border-slate-400"
                          data-testid={`total-hours-${att.id}`}
                        >
                          {computeWorkHours(att.check_in, att.check_out, att.status)}
                        </TableCell>
                        <TableCell className="border-b border-slate-100 dark:border-slate-400">
                          <Badge className={`${statusColors[att.status]} rounded-full px-2 py-0.5 text-xs font-medium`}>
                            {att.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 dark:text-slate-400 text-sm border-b border-slate-100 dark:border-slate-400">
                          <div className="flex flex-col">
                            <span>{att.marked_by || 'Admin'}</span>
                            {att.overridden_by && (
                              <span
                                className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5"
                                title={att.override_notes ? `Reason: ${att.override_notes}` : 'Admin override'}
                                data-testid={`override-info-${att.id}`}
                              >
                                Edited by {att.overridden_by}
                                {att.overridden_at && ` • ${new Date(att.overridden_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right border-b border-slate-100 dark:border-slate-400">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditAttendance({
                              id: att.id,
                              employee_name: att.employee_name,
                              date: att.date,
                              check_in: att.check_in && att.check_in !== "00:00" ? att.check_in : "",
                              check_out: att.check_out && att.check_out !== "00:00" ? att.check_out : "",
                              status: att.status || "Present",
                              notes: att.notes || "",
                            })}
                            data-testid={`edit-attendance-${att.id}`}
                            className="h-8"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              {attendance.filter(att => {
                const matchFrom = !attendanceFilterFromDate || att.date >= attendanceFilterFromDate;
                const matchTo = !attendanceFilterToDate || att.date <= attendanceFilterToDate;
                const matchEmployee = attendanceFilterEmployee === "all" || att.employee_id === attendanceFilterEmployee;
                return matchFrom && matchTo && matchEmployee;
              }).length === 0 && (
                <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                  No attendance records found
                </div>
              )}
            </div>

            {/* Edit Attendance Dialog — admin override for missed check-in/out */}
            <Dialog
              open={!!editAttendance}
              onOpenChange={(open) => { if (!open) setEditAttendance(null); }}
            >
              <DialogContent className="dark:bg-slate-800 dark:border-slate-700" data-testid="edit-attendance-dialog">
                <DialogHeader>
                  <DialogTitle>Edit Attendance</DialogTitle>
                </DialogHeader>
                {editAttendance && (
                  <form onSubmit={handleEditAttendanceSubmit} className="space-y-4">
                    <div className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">
                      <p><span className="font-semibold">Employee:</span> {editAttendance.employee_name}</p>
                      <p><span className="font-semibold">Date:</span> {editAttendance.date}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Check In</Label>
                        <Input
                          type="time"
                          value={editAttendance.check_in}
                          onChange={(e) => setEditAttendance({ ...editAttendance, check_in: e.target.value })}
                          className="rounded-lg"
                          data-testid="edit-att-check-in"
                        />
                      </div>
                      <div>
                        <Label>Check Out</Label>
                        <Input
                          type="time"
                          value={editAttendance.check_out}
                          onChange={(e) => setEditAttendance({ ...editAttendance, check_out: e.target.value })}
                          className="rounded-lg"
                          data-testid="edit-att-check-out"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Status *</Label>
                      <Select
                        value={editAttendance.status}
                        onValueChange={(v) => setEditAttendance({ ...editAttendance, status: v })}
                      >
                        <SelectTrigger className="rounded-lg" data-testid="edit-att-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Present">Present</SelectItem>
                          <SelectItem value="Absent">Absent</SelectItem>
                          <SelectItem value="Half Day">Half Day</SelectItem>
                          <SelectItem value="Leave">Leave</SelectItem>
                          <SelectItem value="Work From Home">Work From Home</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input
                        value={editAttendance.notes}
                        onChange={(e) => setEditAttendance({ ...editAttendance, notes: e.target.value })}
                        placeholder="Reason for override (e.g., Forgot to check out)"
                        className="rounded-lg"
                        data-testid="edit-att-notes"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditAttendance(null)}
                        className="rounded-lg"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-brillien-600 hover:bg-brillien-700 rounded-lg"
                        data-testid="save-attendance-edit"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {canManageHR && (
          <TabsContent value="locations">
            <AttendanceLocations />
          </TabsContent>
        )}

        {myAttendanceStatus?.has_employee_record && (
          <TabsContent value="my-attendance">
            <MyAttendanceList />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
